import React, { useState, useEffect, useMemo, useRef } from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCirclePlus } from "@fortawesome/free-solid-svg-icons";
import { constants, logEducatorDashboard, LogLevel } from "utils";
import { trackClickedButton } from "utils/analytics";
import { archiveLesson, patchGroupAssignments, applyQuestionSet } from "hooks/useCurriculum/useCurriculum";
import { useSessionLength, DEFAULT_SESSION_LENGTH } from "./useSessionLength";
import { useCurriculumClear } from "hooks/useCurriculumClear/useCurriculumClear";
import { ClassroomFilter } from "components/shared/ClassroomFilter/ClassroomFilter";
import { AssignmentAccordion } from "./AssignmentAccordion";
import { oneListCardProps } from "./oneListCards";
import { UnassignedChip } from "./UnassignedCodesRail";
import { ClearGroupCodesModal } from "./ClearGroupCodesModal";
import { ViewQuestionsModal } from "./ViewQuestionsModal";
import { resolveGroupCardTitle } from "utils/groupCardTitle";
import { ReuseFilterModal, reuseFilterCandidates } from "./ReuseFilterModal";
import { ArchiveLessonModal } from "./ArchiveLessonModal";
import { CopyLessonModal } from "./CopyLessonModal";
import { compareCode, signature, toDisplayTrack, toLessonTrack, countDistribution } from "./assignmentDraft";
import { classDisplayName } from "components/shared/ClassroomFilter/groupClassesBySchoolYear";
import { FocusedEditHeader } from "./FocusedEditHeader";

const ASSIGNMENT_TYPE = "Lesson";

const FilterBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  padding: 0 0 16px 0;
`;

const FilterBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const StatusText = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  color: #002E19;
  font-size: 16px;
  & strong { font-weight: 600; }
  & .available { font-size: 14px; font-weight: 400; }
`;

const SecondaryButton = styled.button`
  background: white;
  border: 1px solid #605DEC;
  color: #605DEC;
  height: 48px;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.25px;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:hover:not(:disabled) { background: #F4F4FD; }
`;

// Adapt the lesson + flat assignments into the single-bundle draft shape the
// reused Differentiation accordion expects. Track is mapped to its display form;
// names are joined from the lesson's own alignments.
const buildDraft = (lesson, assignments, lastUpdatedAt = null, poolCodes = []) => {
  if (!lesson || !assignments) return { bundle: null, unassigned: [] };
  const nameFor = (code, track) => {
    const aligns =
      track === "core" ? lesson.addressing :
      track === "prerequisites" ? lesson.building_on :
      lesson.building_toward;
    return aligns?.find((a) => a.standard_code === code)?.standard_name;
  };
  const core = lesson.addressing && lesson.addressing[0];
  const bundle = {
    bundle_type: "lesson",
    // The lesson itself, as the API sends it — the card helper derives the core
    // card's name and its G6U3L2 code from these fields.
    lesson,
    assignment_id: lesson.id,
    primary_standard_code: core?.standard_code || "—",
    // Header title is the lesson name (the eyebrow carries grade/unit/lesson).
    primary_standard_name: lesson.name || core?.standard_name || "—",
    last_updated_at: lastUpdatedAt,
    assignments: assignments
      .filter((a) => a.track_type && a.track_type !== "no_assignment")
      .map((a) => ({
          group_code: a.group_code,
          standard_code: a.standard_code,
          // Core assignments carry the full set of standards they address; prereq/
          // extension leave this null and fall back to the single standard_code.
          standard_codes: a.standard_codes,
          // Completed, not chosen: fill the name the API may omit from the lesson's
          // own alignments. Which name TITLES the card is the helper's call.
          standard_name: a.standard_name || nameFor(a.standard_code, a.track_type) || null,
          track_type: toDisplayTrack(a.track_type),
          // Carries the group's applied Advanced Selection filter so the card can show
          // the "Customized" tag.
          question_filter: a.question_filter,
          // Saved-set pointer: labels the card "My Question Set (n)".
          question_set_id: a.question_set_id,
          // Size of the set the group draws from, already filter-aware server-side.
          // This map is a whitelist, so a field absent here never reaches the card.
          question_count: a.question_count,
          learning_components: a.learning_components,
        })),
  };
  // Unassigned = the classroom-wide free pool (single source of truth for the
  // count and the Add Group Code dropdown), unioned with any of this
  // lesson's own not-yet-released no_assignment codes so none are lost in transit.
  const lessonFree = assignments.filter((a) => a.track_type === "no_assignment").map((a) => a.group_code);
  const unassigned = [...new Set([...(poolCodes || []), ...lessonFree])].sort(compareCode);
  return { bundle, unassigned };
};

export const LessonGroupsView = ({
  lesson,
  assignments,
  assignmentId,
  lastUpdatedAt = null,
  classroomId,
  onRefresh,
  classes,
  selectedClassId,
  onClassChange,
  // Classroom-wide free codes + total (the same values the list header shows) so
  // the "Groups Available" count and the Add Group Code dropdown reflect the whole
  // class pool, not just this lesson's split. Falls back to per-lesson when
  // rendered standalone. Draft/save stay per-lesson.
  poolCodes,
  poolTotal,
  // When rendered inside LessonGroupsList (one per lesson), the shared class
  // filter and "Assign New Lesson" live in the list chrome, so hide this
  // view's own filter bar.
  embedded = false,
  // Copy Lesson (Figma 5059-60948): the account's classes (with per-class
  // assignment_summary) feed the target list; onLessonCopied hands the response's
  // copies up for the success banners; onAccountRefresh re-pulls eligibility
  // after a 400. The kebab only renders when onLessonCopied is wired.
  onLessonCopied,
  onAccountRefresh,
  // Focused edit mode: the list hides its chrome and the other lessons while one is
  // being edited, so it needs to know when this view enters and leaves edit mode.
  onEditingChange,
  // Set on Manage Groups as one list: Figma's card, expanded as the list says.
  listCard,
}) => {
  const history = useHistory();
  const [isEditing, setIsEditingState] = useState(false);
  const setIsEditing = (next) => {
    setIsEditingState(next);
    onEditingChange?.(next);
  };
  const [saving, setSaving] = useState(false);
  const [activeDragCode, setActiveDragCode] = useState(null);
  const [isClearModalOpen, setIsClearModalOpen] = useState(false);
  // Classroom-wide clear (every bundle's codes; released immediately on confirm).
  const { clearableCodes, clearCodes } = useCurriculumClear(classroomId, isClearModalOpen);
  const [viewQuestionsTarget, setViewQuestionsTarget] = useState(null);
  const [reuseOffer, setReuseOffer] = useState(null);
  const [savingQuestionFilter, setSavingQuestionFilter] = useState(false);
  const [questionFilterSaveError, setQuestionFilterSaveError] = useState(null);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  // Synchronous re-entrancy guard: the `archiving` state only disables the button
  // on the next render, so a same-tick double-dispatch could fire a second DELETE
  // on the just-removed assignment (→ 404). The ref blocks that immediately.
  const archivingRef = useRef(false);
  const { sessionLength, onSessionLengthChange: handleSessionLengthChange } =
    useSessionLength(assignments, "LessonGroupsView");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );

  // serverDraft = pristine state from props; draft = local (editable) copy.
  const serverDraft = useMemo(() => buildDraft(lesson, assignments, lastUpdatedAt, poolCodes), [lesson, assignments, lastUpdatedAt, poolCodes]);
  const [draft, setDraft] = useState(serverDraft);
  useEffect(() => { setDraft(serverDraft); }, [serverDraft]);

  // Diagnostic: LessonGroupsView returns null (blank screen) when lesson,
  // assignments, or the built bundle is missing. Log the shape of what we got so
  // a blank Groups view is traceable instead of silent.
  useEffect(() => {
    const willRenderNothing = !lesson || !assignments || !serverDraft.bundle;
    const diag = {
      level: willRenderNothing ? LogLevel.WARNING : LogLevel.INFO,
      event: willRenderNothing ? "lesson_groups_blank_render" : "lesson_groups_render",
      classroomId,
      hasLesson: !!lesson,
      hasAssignments: Array.isArray(assignments),
      assignmentsCount: Array.isArray(assignments) ? assignments.length : null,
      hasBundle: !!serverDraft.bundle,
      lessonId: lesson?.id ?? null,
      lessonName: lesson?.name ?? null,
    };
    logEducatorDashboard("Educator dashboard: LessonGroupsView data received", diag);
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      (willRenderNothing ? console.warn : console.info)(
        willRenderNothing
          ? "[Groups] LessonGroupsView rendered NOTHING (blank) — payload missing lesson/assignments:"
          : "[Groups] LessonGroupsView rendering:",
        diag,
      );
    }
  }, [lesson, assignments, serverDraft, classroomId]);

  // Session length is committed on the spot (handleSessionLengthChange), not via
  // the assignment Save flow, so it's intentionally left out of the dirty check.
  const isDirty = useMemo(() => {
    if (!draft.bundle || !serverDraft.bundle) return false;
    return signature([draft.bundle], draft.unassigned)
      !== signature([serverDraft.bundle], serverDraft.unassigned);
  }, [draft, serverDraft]);

  if (!lesson || !assignments || !draft.bundle) return null;

  // "Grade X, Unit Y, Lesson Z" from the lesson's course/unit/lesson — uppercased
  // as the header eyebrow, title-case in the Copy Lesson modal.
  const lessonLabel = [lesson.course_name, lesson.unit_ordinal_name, lesson.ordinal_name]
    .filter(Boolean)
    .join(", ");
  const lessonEyebrow = lessonLabel.toUpperCase();

  // draft.unassigned is the classroom pool, so the count and the Add Group Code
  // dropdown read the same set (consistent with the list header).
  const availableCount = draft.unassigned.length;
  const totalGroups = typeof poolTotal === "number" ? poolTotal : assignments.length + draft.unassigned.length;
  const guardDirty = () => {
    if (!isDirty) return true;
    // eslint-disable-next-line no-alert
    return window.confirm("You have unsaved changes that will be lost. Continue?");
  };

  // ---- Draft mutations (local only; persisted on Save) ----
  const handleTrash = (code) => {
    setDraft((prev) => ({
      bundle: { ...prev.bundle, assignments: prev.bundle.assignments.filter((a) => a.group_code !== code) },
      unassigned: prev.unassigned.includes(code) ? prev.unassigned : [...prev.unassigned, code].sort(compareCode),
    }));
  };

  // Clear frees codes classroom-wide (any bundle) immediately on confirm, then we
  // reload so the pool + cards reflect it — and re-enter edit mode so the educator
  // can keep reassigning the freed codes without re-opening the editor.
  const handleClearConfirm = async (selection) => {
    setIsClearModalOpen(false);
    try {
      await clearCodes(selection);
      await onRefresh?.();
      setIsEditing(true);
    } catch (e) {
      logEducatorDashboard("LessonGroupsView: failed to clear group codes", { level: LogLevel.WARNING });
    }
  };

  // Duplicate = clone the card's row onto the first free code as a draft entry and
  // enter edit mode, so the teacher reviews (and can retarget/remove) before the
  // normal Save delta commits it.
  const handleDuplicateCard = (card) => {
    setDraft((prev) => {
      const free = prev.unassigned[0];
      if (!free) return prev;
      return {
        bundle: { ...prev.bundle, assignments: [...prev.bundle.assignments, { ...card, group_code: free }] },
        unassigned: prev.unassigned.filter((c) => c !== free),
      };
    });
    setIsEditing(true);
  };


  // ---- Persist (delta: send only changed codes so unchanged ones on the lesson
  // are never re-sent / re-validated against the poaching guard) ----
  const handleSave = async () => {
    if (!isDirty) { setIsEditing(false); return; }
    setSaving(true);
    try {
      const serverByCode = new Map((serverDraft.bundle?.assignments || []).map((a) => [a.group_code, a]));
      const draftByCode = new Map(draft.bundle.assignments.map((a) => [a.group_code, a]));
      const changes = [];
      // Added or re-tracked codes.
      for (const a of draft.bundle.assignments) {
        const prev = serverByCode.get(a.group_code);
        if (!prev || prev.standard_code !== a.standard_code || prev.track_type !== a.track_type) {
          const change = { group_code: a.group_code, track_type: toLessonTrack(a.track_type), standard_code: a.standard_code };
          // Newly added codes (e.g. duplicates) carry their question filter so a
          // "Filtered Questions" / "My Question Set" clone survives intact. Existing
          // codes omit the key — the PATCH leaves their stored filter untouched.
          if (!prev && a.question_filter) change.question_filter = a.question_filter;
          changes.push(change);
        }
      }
      // Removed codes → release to the pool.
      for (const a of (serverDraft.bundle?.assignments || [])) {
        if (!draftByCode.has(a.group_code)) {
          changes.push({ group_code: a.group_code, track_type: null, standard_code: null });
        }
      }
      if (changes.length > 0) {
        // PATCH delta: only the changed codes are sent; untouched codes stay put.
        await patchGroupAssignments(classroomId, assignmentId, changes);
      }

      // A duplicated card carries the source group's saved set, but the delta body
      // has no set pointer — the new code would land on the full bank. Point it at
      // the same set once its assignment exists.
      const newSetTargets = draft.bundle.assignments
        .filter((a) => a.question_set_id && !serverByCode.has(a.group_code))
        .reduce((acc, a) => {
          (acc[a.question_set_id] = acc[a.question_set_id] || []).push(a.group_code);
          return acc;
        }, {});
      await Promise.all(Object.entries(newSetTargets)
        .map(([setId, codes]) => applyQuestionSet(setId, codes)));

      trackClickedButton("Save Group Assignments", {
        page_name: "Dashboard",
        assignment_type: ASSIGNMENT_TYPE,
        source: "lesson_groups_view",
        class: classroomId || null,
      });
      onRefresh?.();
      setIsEditing(false);
    } finally {
      setSaving(false);
    }
  };

  // ---- Navigation to the lesson Edit Group Focus page ----
  const goToLessonFocus = (code) => {
    if (!guardDirty()) return;
    trackClickedButton("Edit Group Focus", {
      page_name: "Dashboard",
      assignment_type: ASSIGNMENT_TYPE,
      source: "lesson_groups_view",
      class: classroomId || null,
      group_code: code || null,
    });
    const params = new URLSearchParams({
      classId: selectedClassId || "",
      classroomId,
      groupCode: code || "",
      // A classroom can run the same lesson in more than one bundle, so the
      // assignment id — not the lesson id — is what identifies the bundle to
      // write to.
      assignmentId: assignmentId || "",
      lessonId: lesson.id,
    });
    history.push(`${constants.LESSON_EDIT_GROUP_FOCUS_PATH}?${params.toString()}`);
  };

  // Into the Question Set view for one group (from the card link or the preview
  // modal's "Create My Question Set" CTA).
  const goToQuestionSet = (code, source) => {
    if (!guardDirty()) return;
    trackClickedButton("Edit Question Set", {
      page_name: "Dashboard",
      assignment_type: ASSIGNMENT_TYPE,
      source,
      class: classroomId || null,
      group_code: code || null,
    });
    const qsParams = new URLSearchParams({
      classId: selectedClassId || "",
      classroomId,
      assignmentId: assignmentId || "",
      groupCode: code,
    });
    history.push(`${constants.QUESTION_SET_PATH}?${qsParams.toString()}`);
  };

  const handleAssignNewLesson = () => {
    if (!guardDirty()) return;
    trackClickedButton("Assign New Lesson", {
      page_name: "Dashboard",
      assignment_type: ASSIGNMENT_TYPE,
      source: "lesson_groups_view",
    });
    const params = new URLSearchParams({
      classroomId,
      currentLessonId: lesson.id,
      groupCodes: assignments.map((a) => a.group_code).join(","),
    });
    history.push(`${constants.VIEW_CHANGE_LESSON_PATH}?${params}`);
  };

  // Change Lesson Focus = swap which lesson this bundle runs, via the proven
  // change-lesson page (keyed by the current lesson + its group codes).
  const onChangeLessonFocus = () => {
    if (!guardDirty()) return;
    trackClickedButton("Change Lesson Focus", {
      page_name: "Dashboard",
      assignment_type: ASSIGNMENT_TYPE,
      source: "lesson_groups_view",
      class: classroomId || null,
      lesson_id: lesson.id || null,
    });
    const bundleCodes = assignments.map((a) => a.group_code);
    // Current per-track distribution — seeds the change modal so it opens on the
    // existing split (clamped to the new lesson's available tracks).
    const dist = countDistribution(assignments);
    // Distributable = the classroom pool plus this bundle's own codes (which the
    // change frees to redistribute), bundle codes first so growing keeps them and
    // shrinking drops pool codes. Threaded whole so the commit can send the actual
    // selected codes as `group_codes` (the bundle can grow past its current codes).
    const availableCodes = [...new Set([...bundleCodes, ...(poolCodes || [])])];
    const params = new URLSearchParams({
      classId: selectedClassId || "",
      classroomId,
      assignmentId,
      currentLessonId: lesson.id,
      groupCodes: bundleCodes.join(","),
      availableCodes: availableCodes.join(","),
      totalAvailable: String(availableCodes.length),
      initCore: String(dist.core),
      initPrereq: String(dist.prerequisites),
      initExt: String(dist.extensions),
    });
    history.push(`${constants.VIEW_CHANGE_LESSON_PATH}?${params}`);
  };

  // Archive = hard-delete the lesson assignment (drops the CLA, frees every code
  // back to the pool, clears prefixes) — same strategy as the standard flow's
  // bundle delete. The class returns to the no-lesson-assigned state.
  const confirmArchive = async () => {
    // In-flight guard: while an archive is running, a duplicate dispatch (double
    // click, re-render, etc.) is dropped so we never fire a second DELETE on the
    // same assignment. Reset in `finally` so a genuine retry after failure works.
    if (!classroomId || !assignmentId || archivingRef.current) return;
    archivingRef.current = true;
    setArchiving(true);
    // Close the modal as soon as the educator confirms; the list is re-fetched in
    // `finally` to reflect the server state regardless of the archive outcome.
    setIsArchiveModalOpen(false);
    const trackArchived = () =>
      trackClickedButton("Archive Lesson", {
        page_name: "Dashboard",
        assignment_type: ASSIGNMENT_TYPE,
        source: "lesson_groups_view",
        class: classroomId || null,
      });
    try {
      await archiveLesson(classroomId, assignmentId);
      trackArchived();
      listCard?.onArchived?.();
    } catch (e) {
      // A 404 means the lesson is already archived — the expected end state, so
      // it counts as an archive. Any other failure is reported to Sentry as a
      // warning (contained, not surfaced as a runtime-error overlay).
      if (e?.status === 404) {
        trackArchived();
        listCard?.onArchived?.();
      } else {
        logEducatorDashboard("LessonGroupsView: failed to archive lesson", {
          level: LogLevel.WARNING,
          event: "archive_error",
          statusCode: e?.status,
          classroomId,
          assignmentId,
        });
      }
    } finally {
      await onRefresh?.();
      archivingRef.current = false;
      setArchiving(false);
    }
  };

  // ---- Drag handling (drop an unassigned code onto the lesson → focus page) ----
  const onDragStart = (e) => setActiveDragCode(e.active?.data?.current?.code || null);
  const onDragCancel = () => setActiveDragCode(null);
  const onDragEnd = (e) => {
    setActiveDragCode(null);
    const code = e.active?.data?.current?.code;
    const overData = e.over?.data?.current;
    if (!code || !overData?.assignmentId) return;
    goToLessonFocus(code);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <div>
        {!embedded && (
          <FilterBar>
            {classes && onClassChange ? (
              <ClassroomFilter
                classes={classes}
                selectedClassId={selectedClassId}
                onClassChange={onClassChange}
                inline
              />
            ) : <span />}
            <FilterBarRight>
              <StatusText>
                <strong>Group Assignments:</strong>
                <span className="available">
                  {availableCount} of {totalGroups} Groups Available
                </span>
              </StatusText>
              <SecondaryButton onClick={handleAssignNewLesson}>
                <FontAwesomeIcon icon={faCirclePlus} />
                Create Lesson Groups
              </SecondaryButton>
            </FilterBarRight>
          </FilterBar>
        )}

        {isEditing && (
          <FocusedEditHeader
            className={classDisplayName(classes?.find((c) => c.id === selectedClassId))}
            availableCount={availableCount}
            totalGroups={totalGroups}
            onClear={totalGroups > availableCount ? () => setIsClearModalOpen(true) : undefined}
          />
        )}

        <AssignmentAccordion
          {...oneListCardProps(listCard)}
          bundle={draft.bundle}
          draftAssignments={draft.bundle.assignments}
          unassignedCodes={draft.unassigned}
          sessionLength={sessionLength ?? DEFAULT_SESSION_LENGTH}
          // Editable in and out of edit mode: the dropdown commits immediately
          // (handleSessionLengthChange), independent of the assignment Save flow.
          sessionLengthEditable
          keepMetaWhileEditing
          eyebrow={lessonEyebrow}
          isEditing={isEditing}
          onEnterEdit={() => setIsEditing(true)}
          onSave={handleSave}
          saving={saving}
          isDirty={isDirty}
          onArchive={() => setIsArchiveModalOpen(true)}
          onCopyLesson={onLessonCopied ? () => setIsCopyModalOpen(true) : undefined}
          onDuplicateCard={handleDuplicateCard}
          onChangeLessonFocus={onChangeLessonFocus}
          onSessionLengthChange={handleSessionLengthChange}
          onAddCode={goToLessonFocus}
          onTrashCode={handleTrash}
          onEditCardStandard={goToLessonFocus}
          onViewQuestions={(card) =>
            setViewQuestionsTarget({
              code: card.standard_code,
              name: card.standard_name,
              // Lesson-scoped filtered preview: Core rows span the
              // lesson's addressing set (no pin); prereq/extension pin their one code.
              questionFilter: card.question_filter,
              lessonStandardCode: card.track_type === "core" ? null : card.standard_code,
              trackType: card.track_type,
              groupCode: card.group_code,
              // A saved set switches the modal to its read-only preview variant.
              questionSetId: card.question_set_id || null,
              focus: resolveGroupCardTitle(card, draft.bundle),
            })
          }
        />
      </div>

      <DragOverlay>{activeDragCode ? <UnassignedChip code={activeDragCode} dragging /> : null}</DragOverlay>


      <ClearGroupCodesModal
        isOpen={isClearModalOpen}
        onClose={() => setIsClearModalOpen(false)}
        clearable={clearableCodes}
        onConfirm={handleClearConfirm}
      />

      <ViewQuestionsModal
        isOpen={!!viewQuestionsTarget}
        onClose={() => { setViewQuestionsTarget(null); setQuestionFilterSaveError(null); }}
        standardCode={viewQuestionsTarget?.code}
        standardName={viewQuestionsTarget?.name}
        questionFilter={viewQuestionsTarget?.questionFilter}
        // An "other" group is a free-form CCSS the teacher pinned, not one of the
        // lesson's alignments — the lesson-scoped route filters it to nothing. Route
        // it like a bare standard so the preview matches the count on the card.
        lessonId={viewQuestionsTarget?.trackType === "other" ? undefined : lesson?.id}
        lessonStandardCode={viewQuestionsTarget?.lessonStandardCode}
        questionSetId={viewQuestionsTarget?.questionSetId}
        groupCode={viewQuestionsTarget?.groupCode}
        focus={viewQuestionsTarget?.focus}
        editable
        saving={savingQuestionFilter}
        saveError={questionFilterSaveError}
        onCreateQuestionSet={() => {
          const code = viewQuestionsTarget?.groupCode;
          if (!code) return;
          setViewQuestionsTarget(null);
          goToQuestionSet(code, "view_questions_modal");
        }}
        onSave={async (nextQuestionFilter, filteredTotal) => {
          const target = viewQuestionsTarget;
          if (!target) return;
          setSavingQuestionFilter(true);
          setQuestionFilterSaveError(null);
          try {
            // Same delta contract as the edit-focus view; question_filter null
            // clears a previously stored filter.
            await patchGroupAssignments(classroomId, assignmentId, [{
              group_code: target.groupCode,
              // The card carries the display track ("extensions"); the endpoint only
              // accepts the lesson form ("extension") and 400s on the plural.
              track_type: toLessonTrack(target.trackType),
              standard_code: target.code,
              question_filter: nextQuestionFilter,
            }]);
            await onRefresh?.();
            setViewQuestionsTarget(null);
            // Other groups on this topic get the same offer the My Question Set
            // flow makes after a save (OKO2-3803).
            const candidates = reuseFilterCandidates(serverDraft.bundle?.assignments, {
              groupCode: target.groupCode,
              trackType: target.trackType,
              standardCode: target.code,
            });
            if (candidates.length > 0) {
              setReuseOffer({
                groupCode: target.groupCode,
                trackType: toLessonTrack(target.trackType),
                standardCode: target.code,
                standardName: target.name,
                filter: nextQuestionFilter,
                total: filteredTotal,
                candidates,
              });
            }
          } catch (e) {
            logEducatorDashboard("LessonGroupsView: failed to save question filter", {
              level: LogLevel.WARNING,
              event: "question_filter_save_error",
              statusCode: e?.status,
              groupCode: target.groupCode,
            });
            setQuestionFilterSaveError(e?.detail || e?.message || "Failed to save filters.");
          } finally {
            setSavingQuestionFilter(false);
          }
        }}
      />

      <ReuseFilterModal
        offer={reuseOffer}
        bundle={serverDraft.bundle || {}}
        classroomId={classroomId}
        assignmentId={assignmentId}
        onClose={() => setReuseOffer(null)}
        onApplied={onRefresh}
      />

      <ArchiveLessonModal
        isOpen={isArchiveModalOpen}
        onClose={() => setIsArchiveModalOpen(false)}
        onConfirm={confirmArchive}
        archiving={archiving}
      />

      <CopyLessonModal
        assignmentType={ASSIGNMENT_TYPE}
        isOpen={isCopyModalOpen}
        onClose={() => setIsCopyModalOpen(false)}
        classroomId={classroomId}
        assignmentId={assignmentId}
        lessonLabel={lessonLabel}
        lessonName={lesson.name}
        classes={classes}
        // One free code needed per real-track group in this bundle (the
        // no_assignment rows are already filtered out of the built bundle).
        requiredCount={serverDraft.bundle?.assignments.length || 0}
        onCopied={(copies) => {
          setIsCopyModalOpen(false);
          onLessonCopied?.(copies, lesson.name);
        }}
        onAccountRefresh={onAccountRefresh}
      />
    </DndContext>
  );
};
